/**
 * Auth Route Configuration
 *
 * Two distinct decisions are made after an OAuth callback:
 *
 * 1. shouldSkipAuthCompletion — skip the /auth/complete API call entirely.
 *    Use for routes that either handle auth themselves (tools, onboarding)
 *    or are already inside the authenticated app shell.
 *
 * 2. shouldSkipPostAuthRedirect — /auth/complete ran successfully, but we
 *    should NOT navigate away from the current page. The user is already
 *    where they need to be (e.g. on a space, an agent thread, a tool page).
 */

// ── shouldSkipAuthCompletion ──────────────────────────────────────────────────

const SKIP_COMPLETION_EXACT: readonly string[] = [
  '/downloads',
  '/work',
  '/workspace',
  '/os-signup',
  '/docs',
  '/seo-blogs',
  '/collegeapp',
  '/essay',
  '/fact-checker',
  '/electron-onboarding',
  '/electron-first-run',
  '/first-run-preview',
  '/blog-auth-callback',
  '/avatar',
  '/avatar2',
  '/avatar3',
  '/terms',
  '/privacy',
  '/about',
  '/contact',
  '/space-templates',
  '/new',
  '/pricing',
];

const SKIP_COMPLETION_PREFIX: readonly string[] = [
  '/app/',
  '/spaces/',
  '/desktops/',
  '/admin/',
  '/showcase/',
  '/tools/',
  '/onboarding/',
  '/standalone/',
  '/entity/',
  '/agents/',
  '/addons/',
  '/book/',
];

/** All routes combined — exported for documentation/debugging. */
export const SKIP_AUTH_COMPLETION_ROUTES: readonly string[] = [
  ...SKIP_COMPLETION_EXACT,
  ...SKIP_COMPLETION_PREFIX,
];

export function shouldSkipAuthCompletion(pathname: string): boolean {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (SKIP_COMPLETION_EXACT.includes(p)) return true;
  const withSlash = p.endsWith('/') ? p : `${p}/`;
  if (SKIP_COMPLETION_EXACT.includes(withSlash)) return true;
  return SKIP_COMPLETION_PREFIX.some(r => p.startsWith(r));
}

// ── shouldSkipPostAuthRedirect ────────────────────────────────────────────────
// These overlap with SKIP_COMPLETION_* but also include routes that *do* need
// /auth/complete (to create user/org) but should not be redirected away from.

const SKIP_REDIRECT_EXACT: readonly string[] = [
  ...SKIP_COMPLETION_EXACT,
  '/river-config',
];

const SKIP_REDIRECT_PREFIX: readonly string[] = [
  ...SKIP_COMPLETION_PREFIX,
  '/blogs/',
  '/blogs',
  '/nsfw',
  '/personas/',
  '/products',
  '/space',    // covers /spaces/ AND /space-* marketing pages
  '/agent',    // covers /agents/ and /agent/*
  '/river4',
  '/ai-personal-computer',
  '/personal-ai',
  '/configure-river-1',
  '/checkout',
  '/getting-started',
  '/for/',
  '/tasks',
  '/workspace',
  '/downloads',
  '/org-payment-success',
  '/payment-success',
];

/**
 * After a successful /auth/complete, should we skip navigating the user away?
 * Returns true when the user is already on the correct destination page.
 */
export function shouldSkipPostAuthRedirect(pathname: string): boolean {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  if (SKIP_REDIRECT_EXACT.includes(p)) return true;
  const withSlash = p.endsWith('/') ? p : `${p}/`;
  if (SKIP_REDIRECT_EXACT.includes(withSlash)) return true;
  return SKIP_REDIRECT_PREFIX.some(r => p.startsWith(r));
}

// ── Auth entry pages (login / signup) ─────────────────────────────────────────
// Session-expiry teardown must no-op on these paths without touching tokens:
// login/signup own those writes, and TokenHandler can re-fire "Login required"
// while a brand-new account is still on /signup.

export const AUTH_ENTRY_PATHS: readonly string[] = [
  '/login',
  '/signup',
  '/selfhosted-login',
  '/app/login',
];

export function isAuthEntryPath(pathname: string): boolean {
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return AUTH_ENTRY_PATHS.some(entry => p === entry || p.startsWith(`${entry}/`));
}

/**
 * Where an expired session should land. Self-hosted deploys serve login at
 * `/selfhosted-login` (and at `/` via RootRoute); every other deploy uses `/login`.
 */
export function getAuthEntryPath(hostname: string = window.location.hostname): string {
  if (hostname.includes('riversystem.ai')) {
    return '/selfhosted-login';
  }
  return '/login';
}

/** sessionStorage key for a one-shot error shown on the login form. */
export const LOGIN_PAGE_ERROR_KEY = 'loginPageError';
/** Prefill the login email after a duplicate-account 409. */
export const LOGIN_PAGE_EMAIL_KEY = 'loginPageEmail';
/** Force the password form (not Google-first) after a duplicate-account 409. */
export const LOGIN_PAGE_METHOD_KEY = 'loginPageMethod';

export type LoginPageMethod = 'password' | 'google';

export type LoginPageHandoff = {
  error: string | null;
  email: string | null;
  method: LoginPageMethod | null;
};

export function stashLoginPageHandoff(handoff: {
  error?: string | null;
  email?: string | null;
  method?: LoginPageMethod | null;
}): void {
  try {
    if (handoff.error) sessionStorage.setItem(LOGIN_PAGE_ERROR_KEY, handoff.error);
    else sessionStorage.removeItem(LOGIN_PAGE_ERROR_KEY);
    if (handoff.email) sessionStorage.setItem(LOGIN_PAGE_EMAIL_KEY, handoff.email);
    else sessionStorage.removeItem(LOGIN_PAGE_EMAIL_KEY);
    if (handoff.method) sessionStorage.setItem(LOGIN_PAGE_METHOD_KEY, handoff.method);
    else sessionStorage.removeItem(LOGIN_PAGE_METHOD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Read and clear a pending login-page handoff (error + email + method).
 */
export function consumeLoginPageHandoff(): LoginPageHandoff {
  const result: LoginPageHandoff = { error: null, email: null, method: null };
  try {
    const error = sessionStorage.getItem(LOGIN_PAGE_ERROR_KEY);
    const email = sessionStorage.getItem(LOGIN_PAGE_EMAIL_KEY);
    const method = sessionStorage.getItem(LOGIN_PAGE_METHOD_KEY);
    if (error) {
      sessionStorage.removeItem(LOGIN_PAGE_ERROR_KEY);
      result.error = error;
    }
    if (email) {
      sessionStorage.removeItem(LOGIN_PAGE_EMAIL_KEY);
      result.email = email;
    }
    if (method === 'password' || method === 'google') {
      sessionStorage.removeItem(LOGIN_PAGE_METHOD_KEY);
      result.method = method;
    }
  } catch {
    /* ignore */
  }
  return result;
}

/**
 * Read and clear a pending login-page error. Returns null when none is set.
 */
export function consumeLoginPageError(): string | null {
  return consumeLoginPageHandoff().error;
}

